import "../global.css";

import { PortalHost } from "@rn-primitives/portal";
import { useFonts } from "expo-font";
import { Stack } from "expo-router";
import { Redirect, usePathname } from "expo-router";
import { StatusBar } from "expo-status-bar";
import * as SystemUI from "expo-system-ui";
import { PropsWithChildren, useEffect } from "react";

import { CormorantGaramond_600SemiBold } from "@expo-google-fonts/cormorant-garamond/600SemiBold";
import { CormorantGaramond_700Bold } from "@expo-google-fonts/cormorant-garamond/700Bold";
import { Manrope_400Regular } from "@expo-google-fonts/manrope/400Regular";
import { Manrope_500Medium } from "@expo-google-fonts/manrope/500Medium";
import { Manrope_600SemiBold } from "@expo-google-fonts/manrope/600SemiBold";
import { Manrope_700Bold } from "@expo-google-fonts/manrope/700Bold";
import { Manrope_800ExtraBold } from "@expo-google-fonts/manrope/800ExtraBold";
import { Newsreader_600SemiBold } from "@expo-google-fonts/newsreader/600SemiBold";
import { Newsreader_700Bold } from "@expo-google-fonts/newsreader/700Bold";

import { AppProvider } from "../src/providers/AppProvider";
import {
  StaffAuthProvider,
  useStaffAuth,
} from "../src/providers/StaffAuthProvider";
import { AppErrorBoundary } from "../src/components/AppErrorBoundary";
import { CentralPerformanceObserver } from "../src/components/CentralPerformanceObserver";
import { bootFirebaseAnalytics } from "../src/lib/firebase";
import {
  cleanStaffPath,
  isCentralHost,
  isPublicWebHost,
} from "../src/lib/hosting";
import {
  CENTRAL_APP_URL,
  isPublicHostPath,
  isRetiredMonarchStaffDemoPath,
} from "../src/lib/hosting-contract";
import { central, palette } from "../src/theme/tokens";

export default function RootLayout() {
  const centralHost = isCentralHost();
  const [fontsLoaded] = useFonts({
    CormorantGaramond_600SemiBold,
    CormorantGaramond_700Bold,
    Manrope_400Regular,
    Manrope_500Medium,
    Manrope_600SemiBold,
    Manrope_700Bold,
    Manrope_800ExtraBold,
    Newsreader_600SemiBold,
    Newsreader_700Bold,
  });

  useEffect(() => {
    SystemUI.setBackgroundColorAsync(
      centralHost ? central.surface.canvas : palette.canvas,
    ).catch(() => undefined);
    if (
      centralHost &&
      (typeof window === "undefined" ||
        !window.location.pathname.startsWith("/sign/"))
    ) {
      bootFirebaseAnalytics().catch(() => undefined);
    }
    if (typeof document !== "undefined") {
      document.title = isCentralHost()
        ? "Central"
        : "WeHonor — More human funeral care";
      const description = document.querySelector('meta[name="description"]');
      if (description) {
        description.setAttribute(
          "content",
          "WeHonor is building a calmer way for families and funeral homes to move through the details that matter most.",
        );
      }
    }
  }, [centralHost]);

  if (!fontsLoaded) {
    return null;
  }

  return (
    <AppErrorBoundary>
      <StaffAuthProvider>
        <AppProvider>
          <CentralPerformanceObserver />
          <CentralHostRouteGuard>
            <StatusBar style="dark" />
            <Stack
              screenOptions={{
                title: centralHost ? "Central" : "WeHonor",
                headerShown: false,
                animation: "none",
                contentStyle: {
                  backgroundColor: centralHost
                    ? central.surface.canvas
                    : palette.canvas,
                },
              }}
            />
          </CentralHostRouteGuard>
          <PortalHost />
        </AppProvider>
      </StaffAuthProvider>
    </AppErrorBoundary>
  );
}

function CentralHostRouteGuard({ children }: PropsWithChildren) {
  const auth = useStaffAuth();
  const pathname = usePathname();
  const cleanPathname = cleanStaffPath(pathname);
  const shouldRedirectPublicHost =
    isPublicWebHost() && !isPublicHostPath(pathname);
  const shouldRedirectRetiredMonarchStaffDemo =
    isPublicWebHost() && isRetiredMonarchStaffDemoPath(pathname);
  const isCleanStaffRoute =
    pathname === "/" ||
    pathname === "/signed-out" ||
    pathname === "/password-reset" ||
    pathname === "/account" ||
    pathname === "/notifications" ||
    pathname === "/registry" ||
    pathname === "/events" ||
    pathname === "/workspace" ||
    pathname.startsWith("/workspace/") ||
    pathname.startsWith("/cases/");
  const isAllowedCentralRoute =
    isCleanStaffRoute ||
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname === "/director" ||
    pathname.startsWith("/director/");

  useEffect(() => {
    if (shouldRedirectRetiredMonarchStaffDemo && typeof window !== "undefined") {
      window.location.replace(CENTRAL_APP_URL);
      return;
    }
    if (shouldRedirectPublicHost && typeof window !== "undefined") {
      window.location.replace("/");
    }
  }, [shouldRedirectPublicHost, shouldRedirectRetiredMonarchStaffDemo]);

  if (
    isCentralHost() &&
    auth.status === "authenticated" &&
    auth.identity?.access.role === "support" &&
    pathname.replace(/\/+$/, "") !== "/workspace/family-access-support"
  ) {
    return <Redirect href="/workspace/family-access-support" />;
  }

  if (isCentralHost() && cleanPathname !== pathname) {
    return <Redirect href={cleanPathname} />;
  }

  if (isCentralHost() && !isAllowedCentralRoute) {
    return <Redirect href="/" />;
  }

  if (shouldRedirectPublicHost || shouldRedirectRetiredMonarchStaffDemo) {
    return null;
  }

  return children;
}
